import {ErrorMessage, GridCell, GridRow} from "./styles/styleds";
import AddressInput from "./AddressInput";
import {AddressType, LocationTab} from "./types/types";
import {
  RoutePlanningActionType,
  RoutePlanningAsyncActionType,
  RoutePlanningDriver,
  useRoutePlanningContext,
  useRoutePlanningDispatch
} from "../../providers/RoutePlanningProvider";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import VehicleIconResolver from "../vehicles/VehicleIconResolver";
import {usePreferenceContext} from "../../providers/PreferenceProvider";
import TimePicker from "../common/date-time/TimePicker";
import {Text} from "@blueprintjs/core";
import {useAppContext} from "../../ApplicationContext";
import {DatePickerMui} from "../common/date-time/DatePickerMui";
import {useCallback, useEffect, useMemo, useState} from "react";
import {debounce} from "lodash";

type DriverInformationItemProps = {
  driver: RoutePlanningDriver;
  activeLocationTab: LocationTab;
  showStartWarehouseButton?: boolean;
  startWarehouseAddress?: string;
  showEndWarehouseButton?: boolean;
  endWarehouseAddress?: string;
  showGpsButton?: boolean;
  gpsDisabled?: boolean;
  gpsTooltip?: string;
  gpsAddress?: string | null;
  manifestLabel?: string;
};

const DriverInformationItem = ({
  driver,
  activeLocationTab,
  showStartWarehouseButton,
  startWarehouseAddress,
  showEndWarehouseButton,
  endWarehouseAddress,
  showGpsButton = false,
  gpsDisabled = false,
  gpsTooltip,
  gpsAddress,
  manifestLabel
}: DriverInformationItemProps) => {
  const {
    driverCode,
    name,
    startLocationType,
    startLocation,
    endLocationType,
    endLocation,
    startDate,
    startTime,
    startTimeZone,
    vehicleDescription,
    isStartDateTimeValid,
    isStartLocationValid,
    isEndLocationValid
  } = driver;
  const driverId = driver.driverId!;
  const manifestDriverId = driver.manifestDriverId ?? undefined;

  const {tenantPreferences} = usePreferenceContext();
  const {
    appState: {isTimeFormat24hr}
  } = useAppContext();
  const {isSubmitted} = useRoutePlanningContext();
  const dispatch = useRoutePlanningDispatch();

  const [startLocationInputValue, setStartLocationInputValue] = useState(startLocation || "");
  const [endLocationInputValue, setEndLocationInputValue] = useState(endLocation || "");

  useEffect(() => {
    if (startLocation !== startLocationInputValue) {
      setStartLocationInputValue(startLocation || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startLocation]);

  useEffect(() => {
    if (endLocation !== endLocationInputValue) {
      setEndLocationInputValue(endLocation || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endLocation]);

  const gridTemplateColumns = useMemo(
    () => `1.5fr 1.5fr 3fr 150px ${isTimeFormat24hr ? "140px" : "160px"} 80px`,

    [isTimeFormat24hr]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedStartLocationDispatch = useCallback(
    debounce((value: string) => {
      dispatch({
        type: RoutePlanningAsyncActionType.UPDATE_START_LOCATION_FOR_DRIVER_ASYNC,
        payload: {driverId, manifestDriverId, startLocation: value}
      });
    }, 500),
    [dispatch]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedEndLocationDispatch = useCallback(
    debounce((value: string) => {
      dispatch({
        type: RoutePlanningAsyncActionType.UPDATE_END_LOCATION_FOR_DRIVER_ASYNC,
        payload: {driverId, manifestDriverId, endLocation: value}
      });
    }, 500),
    [dispatch]
  );

  // Driver item handlers
  const onDriverItemStartLocationChange = useCallback(
    (value: string) => {
      setStartLocationInputValue(value);
      debouncedStartLocationDispatch(value);
    },
    [debouncedStartLocationDispatch]
  );

  const onDriverItemStartLocationTypeChange = useCallback(
    (addressType: AddressType | null) => {
      if (addressType === null) return;
      dispatch({
        type: RoutePlanningAsyncActionType.UPDATE_START_LOCATION_TYPE_FOR_DRIVER_ASYNC,
        payload: {
          driverId,
          manifestDriverId,
          startLocationType: addressType,
          ...(addressType === AddressType.WAREHOUSE && startWarehouseAddress
            ? {startLocation: startWarehouseAddress}
            : {}),
          ...(addressType === AddressType.GPS && gpsAddress ? {startLocation: gpsAddress} : {})
        }
      });
    },
    [dispatch, driverId, manifestDriverId, startWarehouseAddress, gpsAddress]
  );

  const onDriverItemEndLocationChange = useCallback(
    (value: string) => {
      setEndLocationInputValue(value);
      debouncedEndLocationDispatch(value);
    },
    [debouncedEndLocationDispatch]
  );

  const onDriverItemEndLocationTypeChange = useCallback(
    (addressType: AddressType | null) => {
      dispatch({
        type: RoutePlanningAsyncActionType.UPDATE_END_LOCATION_TYPE_FOR_DRIVER_ASYNC,
        payload: {
          driverId,
          manifestDriverId,
          endLocationType: addressType,
          ...(addressType === AddressType.WAREHOUSE && endWarehouseAddress ? {endLocation: endWarehouseAddress} : {})
        }
      });
    },
    [dispatch, driverId, manifestDriverId, endWarehouseAddress]
  );

  const onDriverItemStartDateChange = useCallback(
    (value: any) => {
      dispatch({
        type: RoutePlanningActionType.UPDATE_START_DATE_FOR_DRIVER,
        payload: {driverId, manifestDriverId, startDate: value}
      });
    },
    [dispatch, driverId, manifestDriverId]
  );

  const onDriverItemStartTimeChange = useCallback(
    (value: any) => {
      dispatch({
        type: RoutePlanningActionType.UPDATE_START_TIME_FOR_DRIVER,
        payload: {driverId, manifestDriverId, startTime: value}
      });
    },
    [dispatch, driverId, manifestDriverId]
  );

  const isStartDateTimeValidAndSubmitted = !isSubmitted || isStartDateTimeValid;
  const isStartLocationValidAndSubmitted = !isSubmitted || isStartLocationValid;
  const isEndLocationValidAndSubmitted = !isSubmitted || isEndLocationValid;

  const showStartDateTimeError = !isStartDateTimeValidAndSubmitted;

  return (
    <div data-testid={`driver-item-${driverId}`}>
      <GridRow gridTemplateColumns={gridTemplateColumns}>
        <GridCell
          style={{
            position: "relative",
            height: manifestLabel ? 60 : "auto",
            display: "flex",
            alignItems: manifestLabel ? "center" : "flex-start"
          }}
        >
          {manifestLabel && (
            <span
              style={{
                position: "absolute",
                top: 8,
                left: 8,
                fontSize: 12,
                fontWeight: 600,
                color: "#2B7DE1",
                lineHeight: "12px"
              }}
            >
              {manifestLabel}
            </span>
          )}
          <Text ellipsize>{name}</Text>
        </GridCell>
        <GridCell>
          <Text ellipsize> {driverCode}</Text>
        </GridCell>
        <GridCell>
          {activeLocationTab === LocationTab.START ? (
            <AddressInput
              dataTestIdPrefix={`driver-${driverId}-start-location`}
              address={startLocationInputValue}
              onAddressChange={onDriverItemStartLocationChange}
              addressType={startLocationType}
              onAddressTypeChange={onDriverItemStartLocationTypeChange}
              isValid={isStartLocationValidAndSubmitted}
              showWarehouseButton={showStartWarehouseButton}
              warehouseAddress={startWarehouseAddress}
              showGpsButton={showGpsButton}
              gpsDisabled={gpsDisabled}
              gpsTooltip={gpsTooltip}
            />
          ) : (
            <AddressInput
              dataTestIdPrefix={`driver-${driverId}-end-location`}
              address={endLocationInputValue}
              onAddressChange={onDriverItemEndLocationChange}
              addressType={endLocationType}
              onAddressTypeChange={onDriverItemEndLocationTypeChange}
              allowDeselect
              showStartLocationButton
              isValid={isEndLocationValidAndSubmitted}
              showWarehouseButton={showEndWarehouseButton}
              warehouseAddress={endWarehouseAddress}
            />
          )}
        </GridCell>
        <GridCell>
          <DatePickerMui
            onChange={onDriverItemStartDateChange}
            value={startDate}
            timezone={startTimeZone ?? undefined}
            iconSize={16}
            isValid={isStartDateTimeValidAndSubmitted}
            disabled={true}
          />
        </GridCell>
        <GridCell>
          <TimePicker
            onChange={onDriverItemStartTimeChange}
            value={startTime}
            timezone={startTimeZone ?? undefined}
            showTimeZone={true}
            isValid={isStartDateTimeValidAndSubmitted}
            ampm={!isTimeFormat24hr}
            iconSize={16}
            slotProps={{actionBar: {style: {display: "none"}}}}
          />
        </GridCell>
        <GridCell align={"center"}>
          <FontAwesomeIcon
            data-testid={"vehicle-icon"}
            size={"lg"}
            color={"#797979"}
            icon={VehicleIconResolver(vehicleDescription, tenantPreferences)}
            title={vehicleDescription ?? undefined}
            style={{margin: "auto"}}
          />
        </GridCell>
      </GridRow>
      {(!isStartDateTimeValidAndSubmitted || !isStartLocationValidAndSubmitted || !isEndLocationValidAndSubmitted) && (
        <GridRow
          style={{padding: 0, alignItems: "flex-start"}}
          gridTemplateColumns={gridTemplateColumns}
          id={`error-row-${driverId}`}
          data-testid={`error-row-${driverId}`}
        >
          <>
            <div></div>
            <div></div>
            {activeLocationTab === LocationTab.START ? (
              <ErrorMessage>{isStartLocationValidAndSubmitted ? "" : "Start Location is required."}</ErrorMessage>
            ) : (
              <ErrorMessage>{isEndLocationValidAndSubmitted ? "" : "End Location is required."}</ErrorMessage>
            )}
            <div></div>
            <ErrorMessage>
              {showStartDateTimeError ? "Start Date and Time are required and must not be in the past." : ""}
            </ErrorMessage>
            <div></div>
          </>
        </GridRow>
      )}
    </div>
  );
};

export default DriverInformationItem;
